import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listOntologyTerms, type OntologyModule, type OntologyTermListing } from "../../api/content/ontologyApi";
import { useLoad } from "../../shared/loading/useLoad";
import ModuleList from "./browse/ModuleList";
import { moduleShowing } from "./browse/moduleGroups";
import ModuleTurtle from "./browse/ModuleTurtle";
import TermSearch from "./browse/TermSearch";
import type { OntologySelection } from "./ontologySelection";
import TermDetailCard from "./term/TermDetailCard";
import SliceBar from "./slice/SliceBar";
import type { SliceLens } from "./slice/sliceLens";
import { buildOntologyViewerModel, entityTypeOfKind } from "./viewer/ontologyModel";
import OntologyViewer from "./viewer/OntologyViewer";
import LoadStatus from "../../ui/feedback/LoadStatus";
import StatusMessage from "../../ui/feedback/StatusMessage";

interface OntologyVersionViewProps {
  projectId: string;
  /** The commit of the model version shown. */
  commit: string;
  /** Proposals apply to the latest version, so only it offers them. */
  isLatest: boolean;
  selection: OntologySelection;
  onSelectionChange: (next: OntologySelection) => void;
  /** Terms a failing check of the version names; `null` until its checks have run. */
  failingTerms: ReadonlySet<string> | null;
  /** A Thread's ontology slice: only its terms show, at this version (F95); `null` for the whole model. */
  slice: SliceLens | null;
}

interface VersionTerms {
  /** Every module the version sees, its own and its pinned libraries' (D-08), and all their terms. */
  readonly whole: OntologyTermListing;
  /** The terms shown: of the chosen module, else of the project's own; `null` if it has none. */
  readonly shown: OntologyTermListing | null;
}

async function readVersionTerms(projectId: string, commit: string, moduleId: string | null, signal: AbortSignal): Promise<VersionTerms> {
  const whole = await listOntologyTerms(projectId, commit, null, signal);
  const chosen = whole.modules.find((module) => module.itemId === moduleId);
  const shownIds = chosen === undefined ? whole.modules.filter((module) => module.library === null).map((module) => module.itemId) : [chosen.itemId];
  if (shownIds.length === 0) return { whole, shown: null };
  return { whole, shown: shownIds.length === whole.modules.length ? whole : await listOntologyTerms(projectId, commit, shownIds, signal) };
}

/** One model version: its modules, term search, the term trees and one term's details. The
 * project's own modules show first; what it inherits, such as the DMW basis, is a group of
 * modules to choose (H-09). A version without an ontology module of its own says so, rather
 * than showing OWL's empty roots (F77). Terms a failing check names carry a marker. */
export default function OntologyVersionView({ projectId, commit, isLatest, selection, onSelectionChange, failingTerms, slice }: OntologyVersionViewProps) {
  const { t, i18n } = useTranslation();
  const moduleId = selection.module;
  const load = useCallback((signal: AbortSignal) => readVersionTerms(projectId, commit, moduleId, signal), [projectId, commit, moduleId]);
  const { state, reload } = useLoad(load);
  const loaded = state.status === "loaded" ? state.value : null;
  // A slice shows its own terms from every module the version sees, the DMW basis included.
  const shown = useMemo(
    () => (loaded === null ? null : slice === null ? loaded.shown : { ...loaded.whole, terms: loaded.whole.terms.filter((term) => slice.iris.has(term.iri)) }),
    [loaded, slice],
  );
  const model = useMemo(() => (shown === null ? null : buildOntologyViewerModel(shown, i18n.language)), [shown, i18n.language]);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("ontologyViewer.loading")} loadingClassName="py-10" onRetry={reload} />;
  const { whole } = state.value;
  if (whole.modules.length === 0) {
    return (
      <StatusMessage tone="info" title={t("ontologyViewer.empty.title")}>
        {t("ontologyViewer.empty.body")}
      </StatusMessage>
    );
  }
  const chosenModule = whole.modules.find((module) => module.itemId === moduleId) ?? null;
  const selectedCard = whole.terms.find((term) => term.iri === selection.termIri) ?? null;
  const turtleModule: OntologyModule | null =
    chosenModule ??
    whole.modules.find((module) => module.reference === selectedCard?.module) ??
    whole.modules.find((module) => module.library === null) ??
    null;
  // A linked term opens in the tree of its kind.
  const pane = selectedCard !== null && selection.pane !== "relations" ? entityTypeOfKind(selectedCard.kind) : selection.pane;
  const select = (termIri: string, nextPane = pane) => {
    onSelectionChange({ ...selection, version: commit, termIri, pane: nextPane });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {slice === null ? null : <SliceBar slice={slice} commit={commit} />}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <TermSearch
          projectId={projectId}
          commit={commit}
          onPick={(term) => {
            onSelectionChange({
              ...selection,
              version: commit,
              termIri: term.iri,
              pane: entityTypeOfKind(term.kind),
              module: moduleShowing(whole.modules, term.module),
            });
          }}
        />
        <ModuleList
          modules={whole.modules}
          chosenModuleId={chosenModule?.itemId ?? null}
          onChoose={(module) => {
            onSelectionChange({ ...selection, version: commit, module });
          }}
        />
      </div>
      {model === null ? (
        <StatusMessage tone="info" title={t("ontologyViewer.empty.title")}>
          {t("ontologyViewer.empty.inherits")}
        </StatusMessage>
      ) : (
      <OntologyViewer
        model={model}
        failingIris={failingTerms}
        rawPanel={<ModuleTurtle module={turtleModule} />}
        displayMode={selection.displayMode}
        entityType={pane}
        selectedEntityIri={selection.termIri}
        storageScope={`ontologyViewer:page:${commit}`}
        onDisplayModeChange={(displayMode) => {
          onSelectionChange({ ...selection, version: commit, displayMode });
        }}
        onEntityTypeChange={(nextPane) => {
          onSelectionChange({ ...selection, version: commit, pane: nextPane, termIri: null });
        }}
        onSelectEntity={(termIri, nextPane) => {
          select(termIri, nextPane);
        }}
        renderTermDetails={(entity, lookup) => (
          <TermDetailCard
            key={`${commit}:${entity.iri}`}
            projectId={projectId}
            commit={commit}
            iri={entity.iri}
            canPropose={isLatest}
            knownIris={whole.terms.map((term) => term.iri)}
            modules={whole.modules}
            lookup={lookup}
            editStep="beforeThread"
          />
        )}
      />
      )}
    </div>
  );
}
